// frontend/src/controllers/DocumentViewController.js
import { useState, useCallback, useEffect } from 'react';
import alignmentService from '../services/alignment/alignmentService';
import batchService from '../services/batch/batchService';
import translationService from '../services/translation/translationService';
import projectService from '../services/project/projectService';
import glossaryOptimizerService from '../services/glossary/glossaryOptimizerService';

const useDocumentViewController = () => {
  // State for batch management
  const [batches, setBatches] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // State for editing and saving
  const [savingState, setSavingState] = useState({});
  const [editedBatchIds, setEditedBatchIds] = useState(new Set());
  
  // State for alignments and post-processing
  const [batchAlignments, setBatchAlignments] = useState({});
  const [processingState, setProcessingState] = useState({});
  const [activeWordData, setActiveWordData] = useState(null);
  const [highlightDirection, setHighlightDirection] = useState('none');
  
  // Track alignment requests to prevent redundant processing
  const [alignmentRequestMap, setAlignmentRequestMap] = useState({});
  const [projectLanguages, setProjectLanguages] = useState({ source: 'en', target: 'en' });
  
  /**
   * Request alignments for a batch - handles coordination with alignment service
   * MOVED THIS FUNCTION BEFORE toggleBatchExpansion to fix circular dependency
   */
  const requestBatchAlignments = useCallback((batchId, sourceText, targetText, glossary = [], sourceLanguage, targetLanguage) => {
    console.log(`requestBatchAlignments called for batch ${batchId}`);
    console.log(`Source text length: ${sourceText ? sourceText.length : 0}, Target text length: ${targetText ? targetText.length : 0}`);
    console.log(`Glossary entries: ${glossary ? glossary.length : 0}, Source language: ${sourceLanguage}, Target language: ${targetLanguage}`);
    
    if (!sourceText || !targetText) {
      console.warn("Cannot request alignments with empty source or target text");
      return;
    }
    
    // Log glossary structure
    if (glossary && glossary.length > 0) {
      console.log(`First glossary entry before alignment: ${JSON.stringify(glossary[0])}`);
      console.log(`Glossary entry keys: ${Object.keys(glossary[0])}`);
    } else {
      console.log(`Empty glossary provided for alignment`);
    }
    
    const fullGlossaryForAlignment = glossary;
    
    // This function handles alignment completion callbacks
    const handleAlignmentComplete = (batchId, processorType, result, isComplete) => {
      console.log(`Alignment callback received for batch ${batchId}, processor: ${processorType}, isComplete: ${isComplete}`);
      
      if (!result) {
        console.warn(`No alignment result returned for processor: ${processorType}`);
        return;
      }
      
      // DETAILED LOGGING FOR GLOSSARY RESULTS:
      if (processorType === 'glossary') {
        console.log('Glossary alignment result object:', JSON.stringify(result, null, 2));
        if (result.src2tgt) {
            const nonEmptySrcHighlights = result.src2tgt.filter(h => h && h.length > 0);
            console.log(`Glossary src2tgt: Found ${nonEmptySrcHighlights.length} words with highlights out of ${result.src2tgt.length} total words.`);
            if (nonEmptySrcHighlights.length > 0) {
              console.log('Sample of non-empty src2tgt highlights:', JSON.stringify(nonEmptySrcHighlights.slice(0, 2))); // Log first 2 non-empty
            }
        } else {
          console.warn('Glossary alignment result.src2tgt is undefined or null.');
        }
        if (result.tgt2src) {
          const nonEmptyTgtHighlights = result.tgt2src.filter(h => h && h.length > 0);
          console.log(`Glossary tgt2src: Found ${nonEmptyTgtHighlights.length} words with highlights out of ${result.tgt2src.length} total words.`);
          if (nonEmptyTgtHighlights.length > 0) {
              console.log('Sample of non-empty tgt2src highlights:', JSON.stringify(nonEmptyTgtHighlights.slice(0, 2))); // Log first 2 non-empty
            }
        } else {
          console.warn('Glossary alignment result.tgt2src is undefined or null.');
        }
      }
      // END DETAILED LOGGING
      
      console.log(`Alignment result: processor=${processorType}, type=${result.type}, length=${result.alignments ? result.alignments.length : 'N/A'}`);
      
      // Update alignments state with new results
      setBatchAlignments(prev => {
        const currentAlignments = prev[batchId] || [];
        console.log(`Current alignments for batch ${batchId}: ${currentAlignments.length} processors`);
        
        // Filter out any previous results of the same type
        const filteredAlignments = currentAlignments.filter(a => a.type !== processorType);
        console.log(`Filtered alignments (removed ${currentAlignments.length - filteredAlignments.length} old results): ${filteredAlignments.length}`);
        
        // Add the new result
        const newAlignments = [...filteredAlignments, result];
        console.log(`New alignments count: ${newAlignments.length}`);
        
        return {
          ...prev,
          [batchId]: newAlignments
        };
      });
      
      // Update processing state to reflect completion
      setProcessingState(prev => ({
        ...prev,
        [batchId]: {
          ...(prev[batchId] || {}),
          status: isComplete ? 'completed' : 'processing',
          completedProcessors: [
            ...(prev[batchId]?.completedProcessors || []),
            processorType
          ],
          lastUpdated: Date.now()
        }
      }));
      
      // If all alignment processors are complete, update the request map
      if (isComplete) {
        console.log(`All alignment processors complete for batch ${batchId}`);
        setAlignmentRequestMap(prev => ({
          ...prev,
          [batchId]: {
            timestamp: Date.now(),
            status: 'completed'
          }
        }));
      }
    };
    
    // Request alignments through the alignment service
    console.log(`Requesting alignments from alignmentService for batch ${batchId}`);
    alignmentService.requestAlignments(
      batchId,
      sourceText,
      targetText,
      fullGlossaryForAlignment,  // Pass the full glossary, alignmentService will optimize
      sourceLanguage, 
      targetLanguage, 
      handleAlignmentComplete
    );
  }, []);
  
  /**
   * Load batches for a document - WITHOUT computing alignments
   */
  const loadBatches = useCallback(async (documentId, currentProjectId) => {
    if (!documentId) return;
    
    try {
      setLoading(true);
      const fetchedBatches = await batchService.getBatchesByDocument(documentId);
      setBatches(fetchedBatches);

      // Fetch project languages if projectId is available
      if (currentProjectId) {
        try {
            const projectDetails = await projectService.getProjectById(currentProjectId);
            if (projectDetails && !projectDetails.error) {
                setProjectLanguages({
                    source: projectDetails.source_language || 'en',
                    target: projectDetails.target_language || 'en'
                });
            } else {
                console.warn("Failed to fetch project details for languages, using defaults.");
            }
        } catch (langError) {
            console.error("Error fetching project languages:", langError);
        }
      }
      
      // No automatic alignment computation here - this is the key architectural change
      // Alignments will only be computed when a batch is explicitly expanded
      
      setLoading(false);
      return fetchedBatches;
    } catch (error) {
      console.error("Error loading batches:", error);
      setError("Failed to load document batches");
      setLoading(false);
      return [];
    }
  }, []);
  
  /**
   * Toggle batch expansion with lazy alignment computation
   */
  const toggleBatchExpansion = useCallback(async (batchId, glossary = []) => {
    console.log(`toggleBatchExpansion called for batchId: ${batchId}, glossary entries: ${glossary ? glossary.length : 0}`);
    
    // Log glossary structure
    if (glossary && glossary.length > 0) {
      console.log(`First glossary entry: ${JSON.stringify(glossary[0])}`);
      console.log(`Glossary entry keys: ${Object.keys(glossary[0])}`);
      
      // Check for required keys that might affect highlighting
      const hasSource = glossary.every(item => 'source' in item);
      const hasTarget = glossary.every(item => 'target' in item);
      console.log(`All glossary entries have 'source': ${hasSource}, 'target': ${hasTarget}`);
    }
    
    // If this batch is already expanded, collapse it
    if (expandedBatchId === batchId) {
      console.log(`Collapsing already expanded batch ${batchId}`);
      setExpandedBatchId(null);
      setCurrentBatch(null);
      setActiveWordData(null);
      setHighlightDirection('none');
      
      // Cancel any in-progress alignment computation
      alignmentService.cancelBatchAlignments(batchId);
      
      // Update request map
      setAlignmentRequestMap(prev => {
        const newMap = {...prev};
        delete newMap[batchId];
        return newMap;
      });
      
      return;
    }
    
    // Otherwise, expand this batch
    console.log(`Expanding batch ${batchId}`);
    setExpandedBatchId(batchId);
    
    // Find the batch data
    const selectedBatch = batches.find(batch => batch.id === batchId);
    if (!selectedBatch) {
      console.warn(`Batch with id ${batchId} not found in batches list`);
      return;
    }
    
    console.log(`Selected batch: ${selectedBatch.id}, status: ${selectedBatch.status}`);
    setCurrentBatch(selectedBatch);
    
    // Only compute alignments if:
    // 1. The batch has translated text
    // 2. The batch is either completed or edited
    // 3. We haven't already computed alignments OR we're forcing a refresh
    const hasTranslation = selectedBatch.translated_text && selectedBatch.translated_text.trim() !== '';
    const hasValidStatus = selectedBatch.status === 'completed' || selectedBatch.status === 'edited';
    const needsAlignment = !batchAlignments[batchId] || !alignmentRequestMap[batchId];
    
    console.log(`Alignment check - hasTranslation: ${hasTranslation}, hasValidStatus: ${hasValidStatus}, needsAlignment: ${needsAlignment}`);
    console.log(`Current batchAlignments for ${batchId}: ${batchAlignments[batchId] ? 'exists' : 'does not exist'}`);
    
    if (hasTranslation && hasValidStatus && needsAlignment) {
      console.log(`Requesting alignments for batch ${batchId} with ${glossary.length} glossary entries`);
      
      // Mark this batch as having an alignment request in progress
      setAlignmentRequestMap(prev => ({
        ...prev,
        [batchId]: {
          timestamp: Date.now(),
          status: 'pending'
        }
      }));
      
      // Update processing state to show loading indication
      setProcessingState(prev => ({
        ...prev,
        [batchId]: {
          status: 'pending',
          timestamp: Date.now()
        }
      }));
      
      // Request the alignment computation
      requestBatchAlignments(
        batchId,
        selectedBatch.original_text,
        selectedBatch.translated_text,
        glossary,
        projectLanguages.source, 
        projectLanguages.target 
      );
    } else {
      console.log(`Skipping alignment request - using existing alignments or no valid conditions`);
    }
  }, [expandedBatchId, batches, batchAlignments, alignmentRequestMap, requestBatchAlignments, projectLanguages]);
  
  /**
   * Handle word hover for alignment highlighting
   */
  const handleWordHover = useCallback((wordData) => {
    if (!wordData) {
      setActiveWordData(null);
      setHighlightDirection('none');
      return;
    }
    
    setActiveWordData(wordData);
    setHighlightDirection(wordData.isSource ? 'source-to-target' : 'target-to-source');
  }, []);
  
  /**
   * Handle text changes with autosave functionality
   */
  const handleTextChange = useCallback(async (batchId, text) => {
    // Update batch data in state
    setBatches(prevBatches =>
      prevBatches.map(b => b.id === batchId ? {
        ...b,
        translated_text: text,
      } : b)
    );

    // Update current batch if it's the active one
    setCurrentBatch(prev => {
      if (prev && prev.id === batchId) {
        return {
          ...prev,
          translated_text: text
        };
      }
      return prev;
    });

    // Show saving indicator
    setSavingState(prev => ({
      ...prev,
      [batchId]: {
        status: 'saving',
        timestamp: Date.now()
      }
    }));

    try {
      // Save changes to the database
      const result = await translationService.saveTranslation(batchId, text, "edited");
      
      if (!result.error) {
        // Mark batch as edited
        setEditedBatchIds(prev => new Set([...prev, batchId]));
        
        // Update batch status
        setBatches(prevBatches => 
          prevBatches.map(b => b.id === batchId ? {
            ...b,
            status: "edited"
          } : b)
        );
        
        // Show saved status with auto-dismiss
        setSavingState(prev => ({
          ...prev,
          [batchId]: {
            status: 'saved',
            timestamp: Date.now()
          }
        }));
        
        setTimeout(() => {
          setSavingState(prev => {
            const newState = {...prev};
            // Only clear if it hasn't been updated since
            if (newState[batchId]?.status === 'saved') {
              delete newState[batchId];
            }
            return newState;
          });
        }, 3000);
        
        // Only recompute alignments when text actually changes
        // This preserves the lazy loading architectural approach
        const currentBatch = batches.find(b => b.id === batchId);
        if (currentBatch && text !== currentBatch.translated_text) {
          requestBatchAlignments(
            batchId, 
            currentBatch.original_text, 
            text,
            [], // Assuming glossary is not passed directly here for text change re-alignment or get it if needed
            projectLanguages.source,
            projectLanguages.target
          );
        }
      } else {
        // Show error status
        setSavingState(prev => ({
          ...prev,
          [batchId]: {
            status: 'error',
            message: result.message || 'Save failed',
            timestamp: Date.now()
          }
        }));
      }
    } catch (error) {
      console.error("Error saving translation:", error);
      setSavingState(prev => ({
        ...prev,
        [batchId]: {
          status: 'error',
          message: error.message || 'Save failed',
          timestamp: Date.now()
        }
      }));
    }
  }, [batches, projectLanguages, requestBatchAlignments]);
  
  /**
   * Regenerate translation for a batch
   */
  const regenerate = useCallback(async (batchId, fullProjectGlossary = null) => {
    // Find batch to regenerate
    const batch = batches.find(b => b.id === batchId);
    if (!batch) {
      setError("Batch not found for regeneration");
      return;
    }

    setTranslating(true);
    setError('');
    setSuccessMessage('');

    let optimizedGlossaryForRegen = [];
    if (fullProjectGlossary && fullProjectGlossary.length > 0 && batch && batch.original_text && projectLanguages.source) {
        try {
            console.log(`Optimizing glossary for regeneration of batch ${batchId}. Original size: ${fullProjectGlossary.length}, Source Lang: ${projectLanguages.source}`);
            optimizedGlossaryForRegen = await glossaryOptimizerService.optimizeGlossary(
                batch.original_text,
                fullProjectGlossary,
                projectLanguages.source
            );
            console.log(`Glossary optimized for regeneration. New size: ${optimizedGlossaryForRegen.length}`);
        } catch (optError) {
            console.error(`Error optimizing glossary for regeneration of batch ${batchId}: ${optError}. Using full glossary as fallback.`);
            optimizedGlossaryForRegen = fullProjectGlossary; // Fallback
        }
    } else if (fullProjectGlossary) {
        optimizedGlossaryForRegen = fullProjectGlossary; // Use full if no text or lang for optimization
    }


    try {
      const result = await translationService.regenerateTranslation(batch.id, optimizedGlossaryForRegen); // Use optimized glossary
      
      if (result.error) {
        throw new Error(result.message || 'Regeneration failed');
      }
      
      // Update batch with new translation
      setBatches(prevBatches =>
        prevBatches.map(b => b.id === batch.id ? {
          ...b,
          translated_text: result.translated_text,
          status: "completed"
        } : b)
      );
      
      // Update current batch if it's the active one
      setCurrentBatch(prev => {
        if (prev && prev.id === batch.id) {
          return {
            ...prev,
            translated_text: result.translated_text,
            status: "completed"
          };
        }
        return prev;
      });

      // Clear edited state for this batch
      setEditedBatchIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(batch.id);
        return newSet;
      });

      // Show success message
      const creditsUsed = result.credits_used || 0;
      setSuccessMessage(`Translation regenerated (${creditsUsed} credits used)`);
      
      // Request alignments for the regenerated text
      // Clearing old alignments first ensures we get a fresh computation
      setBatchAlignments(prev => {
        const newState = {...prev};
        delete newState[batch.id];
        return newState;
      });
      
      requestBatchAlignments(
        batch.id, 
        batch.original_text, 
        result.translated_text, 
        fullProjectGlossary, // Pass the original full glossary for re-optimization by alignment service
        projectLanguages.source,
        projectLanguages.target
      );
    } catch (error) {
      console.error("Regeneration error:", error);
      setError(error.message || 'Regeneration failed');
      
      setBatches(prevBatches =>
        prevBatches.map(b => b.id === batch.id ? {
          ...b,
          status: "error"
        } : b)
      );
    } finally {
      setTranslating(false);
    }
  }, [batches, projectLanguages, requestBatchAlignments]);
  
  return {
    // State
    batches,
    currentBatch,
    expandedBatchId,
    loading,
    translating,
    error,
    successMessage,
    savingState,
    batchAlignments,
    processingState,
    activeWordData,
    highlightDirection,
    
    // Actions
    loadBatches,
    toggleBatchExpansion,
    handleWordHover,
    handleTextChange,
    regenerate,
    requestBatchAlignments,
    
    // State setters
    setBatches,
    setCurrentBatch,
    setExpandedBatchId,
    setLoading,
    setTranslating,
    setError,
    setSuccessMessage,
    projectLanguages
  };
};

export default useDocumentViewController;